import { Box, Flex, /*Tag,*/ Text, Divider, VStack, /*HStack, Icon*/ Button, useToast } from "@chakra-ui/react";
import { CheckCircleIcon, TimeIcon, WarningIcon } from "@chakra-ui/icons";
import { ProposalConsignadoCustomStatus } from "../../../../models/consignado/proposal.model";
import { useState } from "react";
import { getNotificacoesTerceirosByProposalId } from "@/services/consignado/proposal-history.service";
import { ProposalHistoryResponseDTO } from "@/services/consignado/dto/proposal-history.dto";
import { ProposalErrorHistory } from "../ProposalErrorHistory/ProposalErrorHistory";
import { GRUPOS_OFERTA_CUSTOM_STATUS } from "@/utils/consignado/ofertaEscolhida";

type Status = "aguardando" | "sucesso" | "erro" | "cancelado";

interface Step {
  status: Status;
  mensagem: string;
}

interface Section {
  [key: string]: Step;
}
const statusIcon: Record<Status, JSX.Element> = {
  cancelado: <WarningIcon color="red.500" boxSize={6} />,
  sucesso: <CheckCircleIcon color="green.500" boxSize={6} />,
  aguardando: <TimeIcon color="blue.400" boxSize={6} />,
  erro: <WarningIcon color="red.500" boxSize={6} />,
};

type SecaoLog = Exclude<keyof ProposalConsignadoCustomStatus, (typeof GRUPOS_OFERTA_CUSTOM_STATUS)[number]>;

const sectionTitles: Record<SecaoLog, string> = {
  confirmacao: "Confirmação",
  formalizacao: "Formalização",
  pagamento: "Pagamento",
  cancelamento: "Cancelamento",
  acoes: "Ações",
  averbacao: "Averbação"
}

export function ProposalLogs({ logs, proposalId }: { logs: ProposalConsignadoCustomStatus; proposalId?: string }) {
  const [isLoading, setIsLoading] = useState(false);
  const [webhookLogs, setWebhookLogs] = useState<ProposalHistoryResponseDTO[]>([]);
  const toast = useToast();

  const formatarData = (data: string | Date): string => {
    const date = typeof data === 'string' ? new Date(data) : data;
    const dia = String(date.getDate()).padStart(2, '0');
    const mes = String(date.getMonth() + 1).padStart(2, '0');
    const ano = String(date.getFullYear()).slice(-2);
    const horas = String(date.getHours()).padStart(2, '0');
    const minutos = String(date.getMinutes()).padStart(2, '0');
    return `${dia}/${mes}/${ano} ${horas}:${minutos}`;
  };

  const formatarMensagemWebhook = (nomeTerceiros: string, resposta: string): string => {
    try {
      const respostaObj = JSON.parse(resposta);
      const respostaFormatada = JSON.stringify(respostaObj, null, 2);
      
      switch (nomeTerceiros.toUpperCase()) {
        case 'BMP':
          return `BMP - ${respostaFormatada}`;
        case 'UNICO':
          return `UNICO - ${respostaFormatada}`;
        default:
          return `${nomeTerceiros} - ${respostaFormatada}`;
      }
    } catch {
      return `${nomeTerceiros} - Resposta recebida`;
    }
  };

  const handleLoadMoreLogs = async () => {
    if (!proposalId) return;
    
    setIsLoading(true);
    try {
      const response = await getNotificacoesTerceirosByProposalId(proposalId)
      
      if (response.length === 0) {
        toast({
          title: "Nenhuma notificação",
          description: "Não há notificações de terceiros para esta proposta.",
          status: "info",
          duration: 4000,
          isClosable: true,
        });
      } else {
        setWebhookLogs(response);
      }
    } catch (error) {
      console.error("Erro ao carregar mais históricos:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getSectionTitle = (sectionKey: SecaoLog): string => {
    return sectionTitles[sectionKey] || sectionKey.charAt(0).toUpperCase() + sectionKey.slice(1);
  };

  const renderSection = (sectionName: SecaoLog, sectionLogs: Section): JSX.Element => (
    <Box key={sectionName} width="100%" mt={4}>
      <Text fontWeight="bold" fontSize="lg" color="blue.600" mb={2}>
        {getSectionTitle(sectionName)}
      </Text>

      <VStack align="start" spacing={2}>
        {Object.entries(sectionLogs).map(([key, step]) => (
          <Flex key={key} align="center" gap={2}>
            {statusIcon[step.status]}
            <Text fontFamily="Lexend Deca">
              {step.mensagem}
            </Text>
          </Flex>
        ))}
      </VStack>
      <Divider mt={2} />
    </Box>
  );

  return (
    <VStack align="start" spacing={6} width="100%">
      {Object.entries(logs)
        .filter(([sectionName]) => !(GRUPOS_OFERTA_CUSTOM_STATUS as readonly string[]).includes(sectionName))
        .map(([sectionName, sectionLogs]) =>
          sectionLogs ? renderSection(sectionName as SecaoLog, sectionLogs) : null
        )}
      
      {webhookLogs.length > 0 && (
        <Box width="100%" mt={4}>
          <Text fontWeight="bold" fontSize="lg" color="blue.600" mb={2}>
            Notificações de Terceiros
          </Text>
          <VStack align="start" spacing={2}>
            {webhookLogs.map((log, index) => (
              <Flex key={index} align="center" gap={2}>
              
                <Text fontFamily="Lexend Deca">
                  {formatarData(log.criado_em)}: {formatarMensagemWebhook(log.nome_terceiros, log.resposta)}
                </Text>
              </Flex>
            ))}
          </VStack>
          <Divider mt={2} />
        </Box>
      )}
      
      {webhookLogs.length === 0 && (
        <Flex width="100%" justifyContent="center" mt={4}>
          <Button 
            bg="#3d5a7f"
            color="white"
            onClick={handleLoadMoreLogs}
            isLoading={isLoading}
            loadingText="Carregando..."
            _hover={{ bg: "#2d4563" }}
          >
            Carregar notificações de terceiros
          </Button>
        </Flex>
      )}

      {proposalId && <ProposalErrorHistory proposalId={proposalId} />}
    </VStack>
  );
}
